import moment, { Moment } from "moment";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import ContainerAnimation from "components/ContainerAnimation";
import { useAuth } from "providers/Auth";
import { useCompany } from "providers/Company";
import { useUser } from "providers/User";
import { api } from "services";
import { RoleDescription } from "types/enums";

import CompanySelect from "./components/CompanySelect";
import ProfessionalSelect from "./components/ProfessionalSelect";
import RangeDatePicker from "./components/RangeDatePicker";
import TaxaHeatmap from "./components/TaxaHeatmap";
import TimeFrameSelect from "./components/TimeFrameSelect";
import * as S from "./styles";
import { useDashboardData } from "./useDashboardData";

enum TimeFrame {
  Today,
  LastWeek,
  LastMonth,
  LastYear,
  Custom,
}

// Intervalo [início, fim] de um preset. Custom retorna null (datas livres).
function rangeForPreset(tf: TimeFrame): { start: Moment; end: Moment } | null {
  const end = moment().endOf("day");
  switch (tf) {
    case TimeFrame.Today:
      return { start: moment().startOf("day"), end };
    case TimeFrame.LastWeek:
      return { start: moment().subtract(6, "days").startOf("day"), end };
    case TimeFrame.LastMonth:
      return { start: moment().subtract(30, "days").startOf("day"), end };
    case TimeFrame.LastYear:
      return { start: moment().subtract(365, "days").startOf("day"), end };
    default:
      return null;
  }
}

export default function Dashboard() {
  const { userRole } = useAuth();
  const { userData } = useUser();
  const {
    professionalCompanies,
    isLoadingProfessionalCompanies,
    getProfessionalCompanies,
  } = useCompany();

  useEffect(() => {
    if (
      userData.id &&
      (userRole === RoleDescription.Professional ||
        userRole === RoleDescription.Secretary)
    ) {
      getProfessionalCompanies(userData.id as number);
    }
  }, [userData]);

  const [indicators, setIndicators] = useState({
    quotePending: 0,
    quoteApproved: 0,
    quoteFinalized: 0,
    quoteCancelled: 0,
  });
  const [isLoadingIndicators, setIsLoadingIndicators] = useState(true);
  const [indicatorsError, setIndicatorsError] = useState(false);

  const [companyId, setCompanyId] = useState<number>(-1);
  const [professionalId, setProfessionalId] = useState<number>(-1);
  const [selectedProfessional, setSelectedProfessional] = useState<{
    value: number;
    label: string;
  } | null>(null);

  const [preset, setPreset] = useState<{ value: TimeFrame; label: string }>({
    value: TimeFrame.LastWeek,
    label: "Última semana",
  });
  const [startDate, setStartDate] = useState<Moment>(() =>
    moment().subtract(6, "days").startOf("day")
  );
  const [endDate, setEndDate] = useState<Moment>(() => moment().endOf("day"));

  // Preset → preenche o intervalo. Editar uma data manualmente → vira "Personalizado".
  const handlePreset = (opt: { value: TimeFrame; label: string }) => {
    setPreset(opt);
    const range = rangeForPreset(opt.value);
    if (range) {
      setStartDate(range.start);
      setEndDate(range.end);
    }
  };
  const handleStartDate = (date: Moment | null) => {
    if (!date || !date.isValid()) return;
    setStartDate(date.clone().startOf("day"));
    setPreset({ value: TimeFrame.Custom, label: "Personalizado" });
  };
  const handleEndDate = (date: Moment | null) => {
    if (!date || !date.isValid()) return;
    setEndDate(date.clone().endOf("day"));
    setPreset({ value: TimeFrame.Custom, label: "Personalizado" });
  };

  const startStr = startDate.format("YYYY-MM-DD");
  const endStr = endDate.format("YYYY-MM-DD");
  const isRangeValid =
    startDate.isValid() && endDate.isValid() && startDate.isSameOrBefore(endDate);
  const periodLabel =
    preset.value === TimeFrame.Custom
      ? `${startDate.format("DD/MM/YYYY")} a ${endDate.format("DD/MM/YYYY")}`
      : preset.label;

  // Dados do mapa de calor (orçamentos concluídos via /Quote/report), sob o
  // filtro de período/instituição/profissional do topo.
  const {
    heatmapRows,
    isLoading: isLoadingDashboardData,
    error: dashboardDataError,
  } = useDashboardData(
    companyId,
    professionalId,
    startStr,
    endStr,
    userRole === RoleDescription.Admin && isRangeValid
  );

  // Contadores básicos (Pendentes/Aprovados/Finalizados/Cancelados) — via
  // /Quote/Dashboards/Date, leve, visível para qualquer papel logado.
  useEffect(() => {
    if (!isRangeValid) return;
    const controller = new AbortController();

    async function getIndicators() {
      setIsLoadingIndicators(true);
      setIndicatorsError(false);
      try {
        const { data } = await api.post(
          "/Quote/Dashboards/Date",
          {
            startDate,
            endDate,
            professionalId: professionalId === -1 ? undefined : professionalId,
            companyId: companyId === -1 ? undefined : companyId,
          },
          { signal: controller.signal }
        );
        setIndicators(data);
      } catch (error: any) {
        if (error?.code === "ERR_CANCELED") return;
        console.error(error);
        setIndicatorsError(true);
      } finally {
        setIsLoadingIndicators(false);
      }
    }
    getIndicators();
    return () => controller.abort();
  }, [companyId, professionalId, startStr, endStr, isRangeValid]);

  return (
    <>
      <ContainerAnimation className="content">
        {/* Header com filtros */}
        <div className="bg-white">
          <div className="container">
            <div className="row justify-content-between align-items-center gx-2 pb-4">
              <div className="col-12 col-lg-auto pb-2">
                <h1 className="mb-0">Dashboard</h1>
              </div>
              <div className="col-12 col-lg-9">
                <div className="row justify-content-lg-end gx-2">
                  <div className="col-12 col-sm-6 col-md-4 pb-2">
                    <TimeFrameSelect
                      value={preset}
                      options={[
                        { value: TimeFrame.Today,     label: "Hoje" },
                        { value: TimeFrame.LastWeek,  label: "Última semana" },
                        { value: TimeFrame.LastMonth, label: "Último mês" },
                        { value: TimeFrame.LastYear,  label: "Último ano" },
                        { value: TimeFrame.Custom,    label: "Personalizado" },
                      ]}
                      defaultValue={{ value: TimeFrame.LastWeek, label: "Última semana" }}
                      setValue={handlePreset}
                    />
                  </div>
                  <div className="col-12 col-sm-6 col-md-4 pb-2">
                    <CompanySelect
                      isLoadingProfessionalCompanies={isLoadingProfessionalCompanies}
                      professionalCompanies={professionalCompanies}
                      setCompanyId={setCompanyId}
                      setProfessionalId={setProfessionalId}
                      setSelectedProfessional={setSelectedProfessional}
                    />
                  </div>
                  {userRole === RoleDescription.Secretary ||
                  userRole === RoleDescription.Admin ? (
                    <div className="col-12 col-md-4 pb-2">
                      <ProfessionalSelect
                        companyId={companyId}
                        setProfessionalId={setProfessionalId}
                        selectedProfessional={selectedProfessional}
                        setSelectedProfessional={setSelectedProfessional}
                      />
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            {/* Linha de intervalo personalizado (De / Até) */}
            {preset.value === TimeFrame.Custom && (
              <div className="row justify-content-lg-end gx-2 pb-4">
                <div className="col-6 col-sm-4 col-md-3 col-lg-2">
                  <RangeDatePicker
                    label="De"
                    value={startDate}
                    onChange={handleStartDate}
                    maxDate={endDate}
                    ariaLabel="Data inicial do período"
                  />
                </div>
                <div className="col-6 col-sm-4 col-md-3 col-lg-2">
                  <RangeDatePicker
                    label="Até"
                    value={endDate}
                    onChange={handleEndDate}
                    minDate={startDate}
                    maxDate={moment().endOf("day")}
                    ariaLabel="Data final do período"
                  />
                </div>
                {!isRangeValid && (
                  <div className="col-12">
                    <p className="small text-danger mb-0 mt-1">
                      <i className="uil uil-exclamation-circle me-1" aria-hidden="true"></i>
                      A data inicial deve ser anterior ou igual à data final.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="container d-flex flex-column gap-5">
          {/* Alerta sem instituições */}
          {userRole !== RoleDescription.Admin && professionalCompanies.length === 0 && (
            <div className="alert alert-warning mb-0 text-center">
              <i className="uil uil-exclamation-triangle me-2"></i>
              <span>
                Seu usuário ainda <b>não possui instituições cadastradas</b>.
                Contacte o administrador para que ele possa cadastrar e vincular uma instituição ou acesse a página de{" "}
                <Link to="/menus" className="fw-bold alert-link btn-link">Menus</Link>
              </span>
            </div>
          )}

          {/* Painel ADM — apenas o mapa de calor Parcelas × Taxa */}
          {userRole === RoleDescription.Admin && (
            <div className="d-flex flex-column gap-4">
              {dashboardDataError && (
                <div className="alert alert-warning d-flex align-items-center gap-2 mb-0" role="alert">
                  <i className="uil uil-exclamation-triangle fs-5 flex-shrink-0"></i>
                  <span>{dashboardDataError}</span>
                </div>
              )}

              <section aria-labelledby="heatmap-title">
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-1">
                  <h2 id="heatmap-title" className="fs-6 text-uppercase text-muted fw-bold mb-0">
                    <i className="uil uil-fire me-1" aria-hidden="true"></i>
                    Mapa de calor · Parcelas × Taxa
                  </h2>
                  <span className="badge rounded-pill bg-light text-primary border border-primary">
                    {periodLabel}
                  </span>
                </div>
                <p className="small text-muted mb-3">
                  Combinações de parcelas e taxa mais usadas no período —{" "}
                  <span className="fw-bold">{periodLabel}</span>.
                </p>
                <div className="card p-3 p-md-4">
                  <TaxaHeatmap rows={heatmapRows} isLoading={isLoadingDashboardData} />
                </div>
              </section>
            </div>
          )}

          {/* Relatório básico — contadores de orçamentos no período, visível para qualquer papel */}
          <section aria-labelledby="indicators-title">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-1">
              <h2 id="indicators-title" className="fs-6 text-uppercase text-muted fw-bold mb-0">
                <i className="uil uil-chart-bar me-1" aria-hidden="true"></i>
                Orçamentos no período
              </h2>
              <span className="badge rounded-pill bg-light text-primary border border-primary">
                {periodLabel}
              </span>
            </div>

            {indicatorsError && (
              <div className="alert alert-danger d-flex align-items-center gap-2" role="alert">
                <i className="uil uil-exclamation-octagon fs-5 flex-shrink-0"></i>
                <span>
                  Não foi possível carregar os indicadores. Verifique sua conexão
                  e tente alterar o período para atualizar.
                </span>
              </div>
            )}

            <div className="row g-3">
              {[
                { label: "Pendentes", value: indicators.quotePending, border: "warning", icon: "uil-hourglass", color: "text-warning" },
                { label: "Aprovados", value: indicators.quoteApproved, border: "success", icon: "uil-check-circle", color: "text-success" },
                { label: "Finalizados", value: indicators.quoteFinalized, border: "info", icon: "uil-bill", color: "text-info" },
                { label: "Cancelados", value: indicators.quoteCancelled, border: "secondary", icon: "uil-times-circle", color: "text-secondary" },
              ].map((card) => (
                <div key={card.label} className="col-6 col-md-3">
                  <div className={`card px-3 pb-3 pt-2 h-100 border-1 border-${card.border} position-relative`}>
                    <i className={`uil ${card.icon} fs-1 opacity-25 position-absolute top-0 end-0 ${card.color}`} aria-hidden="true"></i>
                    <p className="small opacity-75 mb-1 mt-1">{card.label}</p>
                    {isLoadingIndicators ? (
                      <S.Skeleton width="3.5rem" height="2.75rem" aria-label={`Carregando ${card.label}`} />
                    ) : (
                      <span className={`fw-bold display-5 ${card.color}`}>{card.value}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </ContainerAnimation>
    </>
  );
}
